/**
 * Os três cadastros de apoio do estoque: fornecedor, marca e categoria de
 * produto.
 *
 * No protótipo os dois primeiros eram formulários de fachada — campos com
 * `value=""` e `onChange` vazio, e o botão criava sempre um "Novo fornecedor".
 * Aqui eles gravam o que a pessoa digitou, que é o mínimo para o cadastro
 * significar alguma coisa.
 */

import { useEffect, useId, useState } from 'react';

import { Button } from '@ds/core';
import { Alert, Modal } from '@ds/feedback';
import { FormField, Input, Select, Switch } from '@ds/forms';
import { useAviso } from '@/comuns/toast';
import { dados } from '@/data/porta';
import { useAlteracaoPendente } from '@/comuns/useAlteracaoPendente';
import type { CategoriaDeProduto, Fornecedor, Marca } from '@/data/tipos';
import {
    CamposDoFornecedor, formDoFornecedor, salvarFornecedorDoForm, useApoioDoCadastro,
} from '@/screens/contatos/CadastroDeTerceiros';
import type { FormDoFornecedor } from '@/screens/contatos/CadastroDeTerceiros';

export interface FornecedorModalProps {
    alvo: Fornecedor | 'novo' | null;
    onClose: () => void;
    onSalvo: () => void;
}

/** O mesmo cadastro da aba Fornecedores de Contatos (`CadastroDeTerceiros`). */
export function FornecedorModal({ alvo, onClose, onSalvo }: FornecedorModalProps) {
    const aviso = useAviso();
    const apoio = useApoioDoCadastro(Boolean(alvo));
    const [form, setForm] = useState<FormDoFornecedor>(() => formDoFornecedor());
    const formSujo = useAlteracaoPendente(form, alvo);
    const [erro, setErro] = useState<string | null>(null);
    const [salvando, setSalvando] = useState(false);

    useEffect(() => {
        if (!alvo) return;
        setErro(null);
        setForm(formDoFornecedor(alvo === 'novo' ? undefined : alvo));
    }, [alvo]);

    if (!alvo) return null;
    const novo = alvo === 'novo';

    const salvar = async () => {
        setSalvando(true);
        try {
            await salvarFornecedorDoForm(form, novo ? undefined : alvo.id);
            aviso({ title: novo ? 'Fornecedor cadastrado' : 'Fornecedor atualizado', description: form.nome });
            onSalvo();
            onClose();
        } catch (e) {
            setErro(e instanceof Error ? e.message : 'Não foi possível salvar.');
        } finally {
            setSalvando(false);
        }
    };

    return (
        <Modal
            open onClose={onClose} width={560}
            title={novo ? 'Novo fornecedor' : 'Editar fornecedor'}
            description="Fornecedores ficam disponíveis no cadastro de produtos, nas entradas de estoque, em Compras e em Contatos."
            dirty={formSujo}
            onSave={() => void salvar()}
            footer={
                <>
                    <Button variant="secondary" onClick={onClose}>Cancelar</Button>
                    <Button onClick={() => void salvar()} loading={salvando} disabled={!form.nome.trim()}>{novo ? 'Cadastrar' : 'Salvar'}</Button>
                </>
            }
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {(erro || apoio.erro) && <Alert tone="danger" dismissible={false}>{erro ?? apoio.erro}</Alert>}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                    <CamposDoFornecedor
                        form={form} onChange={(m) => setForm((f) => ({ ...f, ...m }))}
                        categorias={apoio.categorias} categoriaAtual={novo ? undefined : alvo.categoria}
                    />
                </div>
            </div>
        </Modal>
    );
}

export interface MarcaModalProps {
    alvo: Marca | 'novo' | null;
    fornecedores: Fornecedor[];
    onClose: () => void;
    onSalvo: () => void;
}

export function MarcaModal({ alvo, fornecedores, onClose, onSalvo }: MarcaModalProps) {
    const aviso = useAviso();
    const idNome = useId();
    const idFabricante = useId();
    const idCategoria = useId();
    const idFornecedor = useId();
    const idRegistro = useId();
    const [form, setForm] = useState({ nome: '', fabricante: '', categoria: '', fornecedorId: '', registro: '', ativa: true });
    const formSujo = useAlteracaoPendente(form, alvo);
    const [erro, setErro] = useState<string | null>(null);
    const [salvando, setSalvando] = useState(false);

    useEffect(() => {
        if (!alvo) return;
        setErro(null);
        setForm(alvo === 'novo'
            ? { nome: '', fabricante: '', categoria: '', fornecedorId: fornecedores.find((f) => f.ativo)?.id ?? '', registro: '', ativa: true }
            : {
                nome: alvo.nome, fabricante: alvo.fabricante ?? '', categoria: alvo.categoria ?? '',
                fornecedorId: alvo.fornecedorId ?? '', registro: alvo.registro ?? '', ativa: alvo.ativa,
            });
    }, [alvo, fornecedores]);

    if (!alvo) return null;
    const novo = alvo === 'novo';

    const salvar = async () => {
        setSalvando(true);
        try {
            await dados().salvarMarca({ ...form, id: novo ? undefined : alvo.id });
            aviso({ title: novo ? 'Marca cadastrada' : 'Marca atualizada', description: form.nome });
            onSalvo();
            onClose();
        } catch (e) {
            setErro(e instanceof Error ? e.message : 'Não foi possível salvar.');
        } finally {
            setSalvando(false);
        }
    };

    return (
        <Modal
            open onClose={onClose} width={520}
            title={novo ? 'Nova marca' : 'Editar marca'}
            description="Marcas organizam os produtos e aparecem no cadastro de insumos."
            dirty={formSujo}
            onSave={() => void salvar()}
            footer={
                <>
                    <Button variant="secondary" onClick={onClose}>Cancelar</Button>
                    <Button onClick={() => void salvar()} loading={salvando} disabled={!form.nome.trim()}>{novo ? 'Cadastrar' : 'Salvar'}</Button>
                </>
            }
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {erro && <Alert tone="danger" dismissible={false}>{erro}</Alert>}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                    <FormField label="Nome da marca" required htmlFor={idNome} style={{ gridColumn: '1 / -1' }}>
                        <Input id={idNome} value={form.nome} onChange={(v) => setForm((f) => ({ ...f, nome: v }))} placeholder="Ex.: Botulift" />
                    </FormField>
                    <FormField label="Fabricante" htmlFor={idFabricante}>
                        <Input id={idFabricante} value={form.fabricante} onChange={(v) => setForm((f) => ({ ...f, fabricante: v }))} />
                    </FormField>
                    <FormField label="Categoria" htmlFor={idCategoria} hint="Como a marca é conhecida — toxina, preenchedor, cosmético">
                        <Input id={idCategoria} value={form.categoria} onChange={(v) => setForm((f) => ({ ...f, categoria: v }))} />
                    </FormField>
                    <FormField label="Fornecedor principal" htmlFor={idFornecedor} style={{ gridColumn: '1 / -1' }}>
                        <Select
                            id={idFornecedor} value={form.fornecedorId} onChange={(v) => setForm((f) => ({ ...f, fornecedorId: v }))}
                            options={fornecedores.map((f) => ({ value: f.id, label: f.ativo ? f.nome : `${f.nome} (inativo)` }))}
                        />
                    </FormField>
                    <FormField label="Registro ANVISA" htmlFor={idRegistro} style={{ gridColumn: '1 / -1' }}>
                        <Input id={idRegistro} value={form.registro} onChange={(v) => setForm((f) => ({ ...f, registro: v }))} placeholder="Opcional" />
                    </FormField>
                </div>
                <Switch
                    checked={form.ativa} label="Marca ativa"
                    description="Inativa não aparece no cadastro de produtos"
                    onChange={(v) => setForm((f) => ({ ...f, ativa: v }))}
                />
            </div>
        </Modal>
    );
}

export interface CategoriaModalProps {
    alvo: CategoriaDeProduto | 'novo' | null;
    onClose: () => void;
    onSalva: () => void;
}

export function CategoriaDeProdutoModal({ alvo, onClose, onSalva }: CategoriaModalProps) {
    const aviso = useAviso();
    const idNome = useId();
    const idDescricao = useId();
    const [form, setForm] = useState({ nome: '', descricao: '', ativa: true });
    const formSujo = useAlteracaoPendente(form, alvo);
    const [erro, setErro] = useState<string | null>(null);
    const [salvando, setSalvando] = useState(false);

    useEffect(() => {
        if (!alvo) return;
        setErro(null);
        setForm(alvo === 'novo'
            ? { nome: '', descricao: '', ativa: true }
            : { nome: alvo.nome, descricao: alvo.descricao ?? '', ativa: alvo.ativa });
    }, [alvo]);

    if (!alvo) return null;
    const novo = alvo === 'novo';

    const salvar = async () => {
        setSalvando(true);
        try {
            await dados().salvarCategoriaDeProduto({ ...form, id: novo ? undefined : alvo.id });
            aviso({ title: novo ? 'Categoria cadastrada' : 'Categoria atualizada', description: form.nome });
            onSalva();
            onClose();
        } catch (e) {
            setErro(e instanceof Error ? e.message : 'Não foi possível salvar.');
        } finally {
            setSalvando(false);
        }
    };

    return (
        <Modal
            open onClose={onClose} width={480}
            title={novo ? 'Nova categoria de produto' : 'Editar categoria'}
            description="Agrupa insumos para relatório, reposição e filtro do cadastro."
            dirty={formSujo}
            onSave={() => void salvar()}
            footer={
                <>
                    <Button variant="secondary" onClick={onClose}>Cancelar</Button>
                    <Button onClick={() => void salvar()} loading={salvando} disabled={!form.nome.trim()}>{novo ? 'Cadastrar' : 'Salvar'}</Button>
                </>
            }
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {erro && <Alert tone="danger" dismissible={false}>{erro}</Alert>}
                <FormField label="Nome" required htmlFor={idNome}>
                    <Input id={idNome} value={form.nome} onChange={(v) => setForm((f) => ({ ...f, nome: v }))} placeholder="Ex.: Injetáveis" />
                </FormField>
                <FormField label="Descrição" htmlFor={idDescricao} hint="Ajuda a equipe a classificar na hora do cadastro">
                    <Input id={idDescricao} value={form.descricao} onChange={(v) => setForm((f) => ({ ...f, descricao: v }))} placeholder="O que entra nesta categoria" />
                </FormField>
                <Switch
                    checked={form.ativa} label="Categoria ativa"
                    description="Categoria inativa não aparece no cadastro de produtos"
                    onChange={(v) => setForm((f) => ({ ...f, ativa: v }))}
                />
                {alvo !== 'novo' && alvo.produtos > 0 && (
                    <Alert tone="info" title={`${alvo.produtos} ${alvo.produtos === 1 ? 'produto usa' : 'produtos usam'} esta categoria`} dismissible={false}>
                        Desativar não move os produtos: eles seguem no estoque, e a categoria só deixa de ser oferecida em novos cadastros.
                    </Alert>
                )}
            </div>
        </Modal>
    );
}
